import React, { useRef, useState } from 'react';
import FilterDropdown, {
  AnchoredPopover,
  FilterCheckList,
  FilterRadioList,
  pickCountSummary,
} from '../Reports/FilterDropdown';
import {
  SLICER_CATALOG,
  WINDOW_HORIZONS,
  XER_CLASSES,
  legendGroups,
  legendLabel,
  p6LegendFields,
  namedSetSummary,
  programsVisibleForPortfolios,
  slicerPickerGroups,
  windowSummary,
} from '../../utils/fabHistogramFilters';
import { unitModeLabel } from '../../utils/fabHistogramPlot';

const FILTER_PANE_W = 220;
const paneChipBtn = {
  height: 24,
  border: '1px solid #e2e8f0',
  background: '#fff',
  borderRadius: 5,
  fontSize: '0.68rem',
  fontWeight: 700,
  color: '#334155',
  padding: '0 8px',
  cursor: 'pointer',
};

function PaneFilter(props) {
  return (
    <FilterDropdown
      fullWidth
      portal
      align="right"
      popoverWidth={300}
      {...props}
    />
  );
}

function setFromToggle(selected, id) {
  const cur = new Set(selected || []);
  const key = String(id);
  if (cur.has(key)) cur.delete(key);
  else cur.add(key);
  return cur;
}

function selectedIds(selected) {
  return selected ? [...selected] : [];
}

function toggleAll(selected, allIds) {
  const keys = (allIds || []).map(String);
  const allOn = keys.length > 0 && keys.every((id) => selected.has(id));
  return allOn ? new Set() : new Set(keys);
}

function filterSummary(selected, opts, oneLabel) {
  if (!opts.length) return 'None';
  if (!selected.size) return 'All';
  return pickCountSummary(selectedIds(selected), opts, oneLabel);
}

export default function FabricationHistogramFilterPane({
  open,
  onClose,
  onOpen,
  p6Source,
  dataSource,
  onSource,
  grain,
  setGrain,
  units,
  setUnits,
  groupBy,
  setGroupBy,
  splitBy,
  setSplitBy,
  legendFields = [],
  showCapacity,
  setShowCapacity,
  showSCurve,
  setShowSCurve,
  showBarTotals,
  setShowBarTotals,
  showMenLabels,
  setShowMenLabels,
  hideCancelled,
  setHideCancelled,
  xerClasses,
  setXerClasses,
  xerInventory,
  projectLabels = null,
  horizon,
  setHorizon,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  slicers,
  portfolios,
  programs,
  shops,
  setShops,
  statuses,
  setStatuses,
  projects,
  setProjects,
  disciplines,
  setDisciplines,
  resources,
  setResources,
  activities,
  setActivities,
  rsrcTypes,
  setRsrcTypes,
  wbsNames,
  setWbsNames,
  calendarNames,
  setCalendarNames,
  portfolioIds = new Set(),
  setPortfolioIds,
  programIds = new Set(),
  setProgramIds,
  columnFilters = {},
  setColumnFilters,
  onDefinePortfolios,
  slicerPanels,
  setSlicerPanels,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const pickerRef = useRef(null);
  const panelOn = (id) => slicerPanels?.[id] !== false && (slicerPanels?.[id] === true
    || SLICER_CATALOG.find((s) => s.id === id)?.defaultOn);

  const setPanel = (id, on) => {
    setSlicerPanels({ ...slicerPanels, [id]: on });
  };

  const shopOpts = slicers.shops || [];
  const statusOpts = slicers.statuses || [];
  const projectOpts = slicers.projects || [];
  const discOpts = slicers.resourceDisciplines || [];
  const resourceOpts = slicers.resources || [];
  const activityOpts = slicers.activities || [];
  const typeOpts = slicers.rsrcTypes || [];
  const wbsOpts = slicers.wbsNames || [];
  const calOpts = slicers.calendarNames || [];
  const portfolioOpts = (portfolios || []).map((p) => ({ id: String(p.id), label: p.name }));
  const programOpts = programsVisibleForPortfolios(programs, portfolios, portfolioIds)
    .map((p) => ({ id: String(p.id), label: p.name }));

  const rows = [];
  const add = (on, node) => { if (on) rows.push(node); };

  add(panelOn('source'), (
    <PaneFilter key="source" label="Source" summary={dataSource === 'p6' ? 'Primavera P6' : dataSource === 'excel' ? 'Excel dumps' : 'Cost breakdown'} active>
      <FilterRadioList
        name="fab-source"
        search={false}
        value={dataSource}
        onChange={onSource}
        groups={[{ items: [
          { id: 'p6', label: 'Primavera P6' },
          { id: 'excel', label: 'Excel data dumps' },
          { id: 'cost_breakdown', label: 'Portal cost breakdown' },
        ] }]}
      />
    </PaneFilter>
  ));
  add(p6Source && panelOn('xer'), (
    <PaneFilter
      key="xer"
      label="XER copy"
      summary={pickCountSummary([...xerClasses], XER_CLASSES, XER_CLASSES.find((x) => xerClasses.has(x.id))?.label)}
      active={xerClasses.size > 0 && xerClasses.size < XER_CLASSES.length}
    >
      <FilterCheckList
        search={false}
        items={XER_CLASSES.map((x) => {
          if (x.id === 'current' && xerInventory?.current != null) {
            return { ...x, label: `Current (${xerInventory.current})` };
          }
          if (x.id === 'every' && xerInventory?.dated != null) {
            return { ...x, label: `Every XER (${xerInventory.dated} dated)` };
          }
          if (x.id === 'latest' && xerInventory?.dated != null) {
            return { ...x, label: `Latest dated (not Current)` };
          }
          return x;
        })}
        selectedIds={[...xerClasses]}
        onToggle={(id) => {
          const next = new Set(xerClasses);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          setXerClasses(next.size ? next : new Set(['current']));
        }}
        onSelectAll={() => setXerClasses(toggleAll(xerClasses, XER_CLASSES.map((x) => x.id)))}
      />
      <div style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.4, marginTop: 6 }}>
        Current is every working copy in P6 — {'{estimate}'}-Current / -CUR and numbered Current-1, -2 … all stack. P6 baselines are never read as projects. Latest dated is the newest weekly file, not Current. Every XER stacks all dated copies — remaining can count twice.
      </div>
    </PaneFilter>
  ));
  add(true, (
    <PaneFilter
      key="window"
      label="Window"
      summary={windowSummary(horizon, dateFrom, dateTo)}
      active={horizon !== 'all' || Boolean(dateFrom || dateTo)}
    >
      <FilterRadioList
        name="fab-window"
        search={false}
        value={horizon}
        onChange={setHorizon}
        groups={[{ items: WINDOW_HORIZONS }]}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
        <label style={{ display: 'block', fontSize: '0.68rem', color: '#475569' }}>
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ ...paneChipBtn, display: 'block', width: '100%', height: 26, marginTop: 3, boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ display: 'block', fontSize: '0.68rem', color: '#475569' }}>
          To
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ ...paneChipBtn, display: 'block', width: '100%', height: 26, marginTop: 3, boxSizing: 'border-box' }}
          />
        </label>
      </div>
      <button
        type="button"
        style={{ ...paneChipBtn, marginTop: 8, width: '100%' }}
        onClick={() => { setDateFrom(''); setDateTo(''); }}
        disabled={!dateFrom && !dateTo}
      >
        Clear dates
      </button>
      <div style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.4, marginTop: 6 }}>
        From today keeps this Sunday and later. All weeks is the full remaining curve. Through last week is history only.
      </div>
    </PaneFilter>
  ));
  add(true, (
    <PaneFilter key="grain" label="Grain" summary={grain === 'week' ? 'Weekly' : grain === 'month' ? 'Monthly' : 'Quarterly'}>
      <FilterRadioList
        name="fab-grain"
        search={false}
        value={grain}
        onChange={(next) => {
          setGrain(next);
          if (units === 'manmonths' && next === 'week') setUnits('manweeks');
        }}
        groups={[{ items: [
          { id: 'week', label: 'Weekly' },
          { id: 'month', label: 'Monthly' },
          { id: 'quarter', label: 'Quarterly' },
        ] }]}
      />
    </PaneFilter>
  ));
  add(true, (
    <PaneFilter key="units" label="Units" summary={unitModeLabel(units)}>
      <FilterRadioList
        name="fab-units"
        search={false}
        value={units}
        onChange={(next) => {
          setUnits(next);
          if (next === 'manmonths' && grain === 'week') setGrain('month');
        }}
        groups={[{ items: [
          { id: 'manmonths', label: 'Man-months' },
          { id: 'manweeks', label: 'Man-weeks' },
          { id: 'hours', label: 'Man-hours' },
        ] }]}
      />
    </PaneFilter>
  ));
  const p6Fields = p6Source ? p6LegendFields(legendFields) : [];
  const legendSearch = p6Fields.length > 4;
  add(panelOn('legend'), (
    <PaneFilter
      key="legend"
      label="Legend"
      summary={splitBy && splitBy !== 'none' && splitBy !== groupBy
        ? `${legendLabel(groupBy, p6Fields)} · ${legendLabel(splitBy, p6Fields)}`
        : legendLabel(groupBy, p6Fields)}
      active={groupBy !== 'workbook' || (splitBy && splitBy !== 'none')}
    >
      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', marginBottom: 4 }}>
        COLOUR BY
      </div>
      <FilterRadioList
        name="fab-legend"
        value={groupBy}
        search={legendSearch}
        onChange={(next) => {
          setGroupBy(next);
          if (splitBy === next) setSplitBy('none');
        }}
        groups={legendGroups(p6Fields)}
      />
      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', margin: '10px 0 4px' }}>
        ALSO SPLIT BY
      </div>
      <FilterRadioList
        name="fab-legend-split"
        value={splitBy || 'none'}
        search={legendSearch}
        onChange={setSplitBy}
        groups={(() => {
          const groups = legendGroups(p6Fields, { omit: ['total', groupBy] });
          const none = { id: 'none', label: 'None — one colour key' };
          if (!groups.length) return [{ items: [none] }];
          return [{ ...groups[0], items: [none, ...groups[0].items] }, ...groups.slice(1)];
        })()}
      />
      <div style={{ fontSize: '0.62rem', color: '#64748b', lineHeight: 1.4, marginTop: 6 }}>
        Two keys at once become one stack slice each — Project ID · Resource. Colour still follows the first key’s name.
        {p6Source && (
          <>
            {' '}P6 activity codes, UDFs and resource codes (Resource Standardization) list here when the selected copies carry values — same catalog as the EVM report legend.
          </>
        )}
      </div>
    </PaneFilter>
  ));
  add(panelOn('portfolio'), (
    <PaneFilter
      key="portfolio"
      label="Portfolio"
      summary={namedSetSummary(portfolioIds, portfolioOpts)}
      active={portfolioIds.size > 0}
    >
      <FilterCheckList
        items={portfolioOpts}
        selectedIds={selectedIds(portfolioIds)}
        onToggle={(id) => setPortfolioIds(setFromToggle(portfolioIds, id))}
        onSelectAll={() => setPortfolioIds(toggleAll(portfolioIds, portfolioOpts.map((p) => p.id)))}
        empty="Define a portfolio first. A portfolio is a set of programs."
      />
      {onDefinePortfolios && (
        <button type="button" onClick={onDefinePortfolios} style={{ ...paneChipBtn, marginTop: 6, width: '100%' }}>
          Define programs / portfolios
        </button>
      )}
    </PaneFilter>
  ));
  add(panelOn('program'), (
    <PaneFilter
      key="program"
      label="Program"
      summary={namedSetSummary(programIds, programOpts)}
      active={programIds.size > 0}
    >
      <FilterCheckList
        items={programOpts}
        selectedIds={selectedIds(programIds)}
        onToggle={(id) => setProgramIds(setFromToggle(programIds, id))}
        onSelectAll={() => setProgramIds(toggleAll(programIds, programOpts.map((p) => p.id)))}
        empty="Define a program first. A program is a set of projects."
      />
      {onDefinePortfolios && (
        <button type="button" onClick={onDefinePortfolios} style={{ ...paneChipBtn, marginTop: 6, width: '100%' }}>
          Define programs / portfolios
        </button>
      )}
    </PaneFilter>
  ));
  add(panelOn('project'), (
    <PaneFilter key="project" label="Project" summary={filterSummary(projects, projectOpts)} active={projects.size > 0} popoverWidth={760}>
      <FilterCheckList items={projectOpts.map((o) => ({ id: o, label: projectLabels?.[o] || o }))} selectedIds={selectedIds(projects)} search={projectOpts.length > 8} onToggle={(id) => setProjects(setFromToggle(projects, id))} onSelectAll={() => setProjects(toggleAll(projects, projectOpts))} />
    </PaneFilter>
  ));
  add(panelOn('discipline'), (
    <PaneFilter key="discipline" label="Resource discipline" summary={filterSummary(disciplines, discOpts)} active={disciplines.size > 0}>
      <FilterCheckList items={discOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(disciplines)} onToggle={(id) => setDisciplines(setFromToggle(disciplines, id))} onSelectAll={() => setDisciplines(toggleAll(disciplines, discOpts))} />
    </PaneFilter>
  ));
  add(panelOn('rsrcType'), (
    <PaneFilter key="rsrcType" label="Resource type" summary={filterSummary(rsrcTypes, typeOpts)} active={rsrcTypes.size > 0}>
      <FilterCheckList items={typeOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(rsrcTypes)} search={false} onToggle={(id) => setRsrcTypes(setFromToggle(rsrcTypes, id))} onSelectAll={() => setRsrcTypes(toggleAll(rsrcTypes, typeOpts))} />
    </PaneFilter>
  ));
  add(panelOn('shop'), (
    <PaneFilter key="shop" label="Shop" summary={filterSummary(shops, shopOpts)} active={shops.size > 0}>
      <FilterCheckList items={shopOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(shops)} onToggle={(id) => setShops(setFromToggle(shops, id))} onSelectAll={() => setShops(toggleAll(shops, shopOpts))} />
    </PaneFilter>
  ));
  add(panelOn('status'), (
    <PaneFilter key="status" label="Status" summary={filterSummary(statuses, statusOpts)} active={statuses.size > 0}>
      <FilterCheckList items={statusOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(statuses)} search={false} onToggle={(id) => setStatuses(setFromToggle(statuses, id))} onSelectAll={() => setStatuses(toggleAll(statuses, statusOpts))} />
    </PaneFilter>
  ));
  add(panelOn('resource'), (
    <PaneFilter key="resource" label="Resource" summary={filterSummary(resources, resourceOpts)} active={resources.size > 0}>
      <FilterCheckList items={resourceOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(resources)} onToggle={(id) => setResources(setFromToggle(resources, id))} onSelectAll={() => setResources(toggleAll(resources, resourceOpts))} />
    </PaneFilter>
  ));
  add(panelOn('activity'), (
    <PaneFilter key="activity" label="Activity" summary={filterSummary(activities, activityOpts)} active={activities.size > 0}>
      <FilterCheckList items={activityOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(activities)} onToggle={(id) => setActivities(setFromToggle(activities, id))} onSelectAll={() => setActivities(toggleAll(activities, activityOpts))} />
    </PaneFilter>
  ));
  add(panelOn('wbs'), (
    <PaneFilter key="wbs" label="WBS" summary={filterSummary(wbsNames, wbsOpts)} active={wbsNames.size > 0}>
      <FilterCheckList items={wbsOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(wbsNames)} onToggle={(id) => setWbsNames(setFromToggle(wbsNames, id))} onSelectAll={() => setWbsNames(toggleAll(wbsNames, wbsOpts))} />
    </PaneFilter>
  ));
  add(panelOn('calendar'), (
    <PaneFilter key="calendar" label="P6 calendar" summary={filterSummary(calendarNames, calOpts)} active={calendarNames.size > 0}>
      <FilterCheckList items={calOpts.map((o) => ({ id: o, label: o }))} selectedIds={selectedIds(calendarNames)} onToggle={(id) => setCalendarNames(setFromToggle(calendarNames, id))} onSelectAll={() => setCalendarNames(toggleAll(calendarNames, calOpts))} />
    </PaneFilter>
  ));
  if (p6Source) {
    const columnOptions = slicers.columns || {};
    p6LegendFields(legendFields).forEach((field) => {
      if (!panelOn(field.id)) return;
      const opts = columnOptions[field.id] || [];
      const selected = columnFilters[field.id] || new Set();
      add(true, (
        <PaneFilter key={field.id} label={field.label} summary={filterSummary(selected, opts)} active={selected.size > 0}>
          <FilterCheckList
            items={opts.map((o) => ({ id: o, label: o }))}
            selectedIds={selectedIds(selected)}
            onToggle={(id) => setColumnFilters((prev) => ({
              ...prev,
              [field.id]: setFromToggle(prev[field.id] || new Set(), id),
            }))}
            onSelectAll={() => setColumnFilters((prev) => ({
              ...prev,
              [field.id]: toggleAll(prev[field.id] || new Set(), opts),
            }))}
          />
        </PaneFilter>
      ));
    });
  }

  const pickerGroups = slicerPickerGroups(legendFields, { p6Source }).map((group) => {
    const q = pickerSearch.trim().toLowerCase();
    const items = group.items.filter((f) => {
      if (!q) return true;
      return [f.label, f.id, f.group, group.label].some((x) => String(x || '').toLowerCase().includes(q));
    });
    return { ...group, items };
  }).filter((group) => group.items.length);

  return (
    <>
      <aside
        aria-hidden={!open}
        style={{
          flex: `0 0 ${open ? FILTER_PANE_W : 0}px`,
          width: open ? FILTER_PANE_W : 0,
          minWidth: 0,
          maxHeight: 'calc(100vh - 140px)',
          marginLeft: open ? 8 : 0,
          marginTop: 6,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          overflow: 'hidden',
          position: 'sticky',
          top: 8,
          alignSelf: 'flex-start',
          boxSizing: 'border-box',
          transition: 'flex-basis 220ms cubic-bezier(0.22, 1, 0.36, 1), width 220ms cubic-bezier(0.22, 1, 0.36, 1), margin-left 200ms ease, opacity 180ms ease',
        }}
      >
        <div style={{
          width: FILTER_PANE_W,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          maxHeight: 'calc(100vh - 148px)',
          overflow: 'auto',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 10px',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            background: '#f8fafc',
            zIndex: 1,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#334155' }}>
              Filters
            </div>
            <span style={{ flex: 1 }} />
            <div ref={pickerRef}>
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                style={{ ...paneChipBtn, background: pickerOpen ? '#e2e8f0' : '#fff' }}
              >
                + Add slicer {pickerOpen ? '▴' : '▾'}
              </button>
              <AnchoredPopover
                open={pickerOpen}
                anchorRef={pickerRef}
                width={280}
                align="right"
                maxHeight={380}
                zIndex={90}
                onClose={() => setPickerOpen(false)}
              >
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Add a slicer</div>
                <div style={{ fontSize: '0.62rem', color: '#64748b', marginBottom: 6, lineHeight: 1.35 }}>
                  Same columns as the legend. Check one to pin it here. Uncheck to hide it.
                </div>
                <input
                  type="search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search columns…"
                  style={{
                    width: '100%', boxSizing: 'border-box', height: 26, marginBottom: 8,
                    border: '1px solid #cbd5e1', borderRadius: 4, padding: '0 8px', fontSize: '0.72rem',
                  }}
                />
                {pickerGroups.map((group) => (
                    <div key={group.label} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', marginBottom: 3 }}>
                        {group.label.toUpperCase()}
                      </div>
                      {group.items.map((f) => (
                        <label key={f.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem',
                          padding: '0.22rem 0.15rem', cursor: 'pointer', color: '#334155',
                        }}>
                          <input
                            type="checkbox"
                            checked={panelOn(f.id)}
                            onChange={() => setPanel(f.id, !panelOn(f.id))}
                            style={{ accentColor: '#475569', width: 13, height: 13 }}
                          />
                          {f.label}
                        </label>
                      ))}
                    </div>
                ))}
              </AnchoredPopover>
            </div>
            <button type="button" onClick={onClose} style={paneChipBtn}>Close</button>
          </div>
          <div style={{ padding: '10px 10px 12px' }}>
            {rows}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#334155', marginBottom: 6 }}>
              <input type="checkbox" checked={showCapacity} onChange={(e) => setShowCapacity(e.target.checked)} />
              Capacity line
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#334155', marginBottom: 6 }}>
              <input type="checkbox" checked={showSCurve} onChange={(e) => setShowSCurve(e.target.checked)} />
              S-curve (hours)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#334155', marginBottom: 6 }}>
              <input type="checkbox" checked={showBarTotals} onChange={(e) => setShowBarTotals(e.target.checked)} />
              Bar totals
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#334155', marginBottom: 6 }}>
              <input type="checkbox" checked={showMenLabels} onChange={(e) => setShowMenLabels(e.target.checked)} />
              Men on bars
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#334155' }}>
              <input type="checkbox" checked={hideCancelled} onChange={(e) => setHideCancelled(e.target.checked)} />
              Hide cancelled
            </label>
          </div>
        </div>
      </aside>
      {!open && (
        <button
          type="button"
          onClick={onOpen}
          title="Open filters"
          style={{
            position: 'absolute',
            right: 0,
            top: 12,
            zIndex: 20,
            width: 28,
            border: '1px solid #cbd5e1',
            background: '#f8fafc',
            borderRadius: '6px 0 0 6px',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 800,
            color: '#334155',
            letterSpacing: 0.6,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            padding: '10px 4px',
          }}
        >
          Filters
        </button>
      )}
    </>
  );
}

export { FILTER_PANE_W };
